import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlignJustify, Copy, Download, Plus, Upload } from "lucide-react";
import { ReactComponent as TrashIcon } from "@/assets/images/trash-icon.svg";
import { ItemSheetLatest } from "@/types/itemsheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ReactComponent as PinIcon } from "@/assets/images/pin-true.svg";
import { ReactComponent as PinFalseIcon } from "@/assets/images/pin-false.svg";
import { cn } from "@/libs/utils";
import { useMemo, useRef, useState } from "react";
import {
  useIsEditing,
  useIsExcelDownloading,
  useSetIsExcelDownloading,
} from "../../hooks/useTableStore";
import EstimateTabsCreate from "./EstimateTabsCreate";
import { useEstimateActions } from "../../hooks/useTabsActions";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useOverwriteProjectGroupsItemSheetFromFile } from "@/libs/react-query/hooks/useItemsheetsQuery";
import { useParams } from "react-router-dom";
import { useSelectedProjectInfo } from "../../hooks/useTabStore";
import { showToast } from "@/utils/toastUtils";

type Props = {
  latests: ItemSheetLatest[];
  handleExcelDownload?: () => void;
};
export default function EstimateTabs({ latests, handleExcelDownload }: Props) {
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const [alertOpenId, setAlertOpenId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentLatestId, setCurrentLatestId] = useState<string | null>(null);

  const { group_id } = useParams<{ group_id: string }>();
  const selectedProjectInfo = useSelectedProjectInfo();
  const isEditing = useIsEditing();
  const isExcelDownloading = useIsExcelDownloading();
  const setExcelDownloading = useSetIsExcelDownloading();

  const { mutate: overwriteExcel } = useOverwriteProjectGroupsItemSheetFromFile(
    group_id || ""
  );

  const { handleConfirm, handleCopy, handleDelete } = useEstimateActions({
    closePopover: () => setOpenPopoverId(null),
  });

  const isAlreadyFinalized = useMemo(
    () => latests.some((latest) => latest.is_finalized),
    [latests]
  );

  const handleConfirmFinalization = (
    latestId: string,
    is_finalized: boolean
  ) => {
    handleConfirm(latestId, is_finalized);
    setAlertOpenId(null);
    setOpenPopoverId(null);
  };

  const handleUploadClick = (latestId: string) => {
    setCurrentLatestId(latestId);
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentLatestId || !group_id || !selectedProjectInfo) return;

    const currentLatest = latests.find(
      (latest) => latest.id === currentLatestId
    );
    if (!currentLatest) return;

    const { projectId, projectType } = selectedProjectInfo;

    overwriteExcel(
      {
        latest_sheet_id: currentLatestId,
        data: {
          project_id: projectId,
          project_type: projectType,
          original_id: currentLatest.original_id,
          uploaded_file: file,
        },
      },
      {
        onSuccess: () => {
          showToast({ message: "EXCEL 업로드가 완료되었습니다." });
        },
        onError: (error: any) => {
          showToast({
            message: "EXCEL 업로드에 실패했습니다.",
            type: "error",
            description: error?.response?.data?.display_message || "",
          });
        },
      }
    );

    setOpenPopoverId(null);
    setCurrentLatestId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const actionInfo = (
    latestId: string,
    is_finalized: boolean,
    onConfirmFinalization: () => void
  ) => [
    {
      label: (
        <span className="text-[8px] text-gray-A font-semibold">복제하기</span>
      ),
      icon: <Copy size={14} className="text-gray-A" />,
      onClick: () => handleCopy(latestId),
    },
    {
      label: (
        <span
          className={cn(
            "text-[8px] font-semibold",
            is_finalized ? "text-blue-ThermoFisher" : "text-blue-B"
          )}
        >
          {is_finalized ? "확정 해제" : "산출내역서 확정"}
        </span>
      ),
      icon: is_finalized ? (
        <PinFalseIcon className="size-3.5" />
      ) : (
        <PinIcon className="size-3.5" />
      ),
      onClick: () => {
        if (!is_finalized && isAlreadyFinalized) {
          setAlertOpenId(latestId);
        } else {
          onConfirmFinalization();
        }
      },
    },
    {
      label: (
        <span className="text-[8px] text-black font-semibold">
          EXCEL 업로드
        </span>
      ),
      icon: <Upload size={12} className="text-dark-red" />,
      onClick: () => handleUploadClick(latestId),
    },
    {
      label: (
        <span className="text-[8px] text-black font-semibold">
          EXCEL 다운로드
        </span>
      ),
      icon: <Download size={12} className="text-green" />,
      onClick: () => {
        setExcelDownloading(true);
        setOpenPopoverId(null);
      },
    },
    {
      label: <span className="text-[8px] text-pink font-semibold">삭제</span>,
      icon: <TrashIcon className="size-3.5 text-pink" />,
      onClick: () => handleDelete(latestId),
      disabled: latests.length <= 1,
    },
  ];

  return (
    <div className="flex justify-between items-center py-4 px-5 border-b border-gray-D overflow-x-auto">
      <TabsList>
        {latests.map((latest) => (
          <div className="inline-flex items-center gap-2" key={latest.id}>
            <TabsTrigger
              value={latest.id}
              disabled={isEditing || isExcelDownloading}
              className="flex gap-1"
            >
              {latest.is_finalized && (
                <span className="bg-blue-B px-2 py-[6px] text-white rounded-full">
                  확정
                </span>
              )}
              {latest.title}
            </TabsTrigger>

            <Popover
              open={openPopoverId === latest.id}
              onOpenChange={(isOpen) =>
                setOpenPopoverId(isOpen ? latest.id : null)
              }
            >
              <PopoverTrigger disabled={isEditing || isExcelDownloading}>
                <AlignJustify className="size-4 text-gray-A" />
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <div className="flex flex-col">
                  {actionInfo(latest.id, latest.is_finalized ?? false, () =>
                    handleConfirmFinalization(
                      latest.id,
                      latest.is_finalized ?? false
                    )
                  ).map((action, index) => (
                    <div
                      key={`${latest.id}-action-${index}`}
                      className={cn(
                        "px-5 py-2 flex items-center gap-2 cursor-pointer hover:bg-gray-E",
                        action.disabled ? "opacity-50" : ""
                      )}
                      onClick={() => {
                        if (!action.disabled) {
                          action.onClick?.();
                        }
                      }}
                    >
                      {action.icon}
                      {action.label}
                    </div>
                  ))}

                  <AlertDialog
                    open={alertOpenId === latest.id}
                    onOpenChange={() => setAlertOpenId(null)}
                  >
                    <AlertDialogContent className="w-[639px] h-[222px]">
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          이미 확정된 산출내역서가 있습니다
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          선택하신 산출내역서로 확정을 변경하시겠어요?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel asChild>
                          <Button
                            variant="cancel"
                            size="footer"
                            className="px-5 py-2 h-auto leading-none"
                            onClick={() => {
                              setOpenPopoverId(null);
                              setAlertOpenId(null);
                            }}
                          >
                            취소하기
                          </Button>
                        </AlertDialogCancel>
                        <AlertDialogAction asChild>
                          <Button
                            variant="confirm"
                            size="footer"
                            className="px-5 py-2 h-auto leading-none bg-blue-B border-blue-B hover:bg-blue-B/80"
                            onClick={() =>
                              handleConfirmFinalization(
                                latest.id,
                                latest.is_finalized ?? false
                              )
                            }
                          >
                            변경하기
                          </Button>
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        ))}

        <EstimateTabsCreate />
      </TabsList>

      {!isEditing && isExcelDownloading && (
        <div className="ml-4 inline-flex items-center gap-4 whitespace-nowrap">
          <div className="flex items-center gap-3">
            <Button
              size="trigger"
              variant="cancel"
              onClick={() => setExcelDownloading(false)}
            >
              취소하기
            </Button>
            <Button
              size="trigger"
              variant="confirm"
              onClick={handleExcelDownload}
            >
              다운로드
            </Button>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx, .xls, .csv"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}
